import { TimelineItem } from '@/types/item';

export interface ItemMilestone {
  id: string;
  item_id: string;
  start_date: string;
  end_date: string;
  completion_percentage: number;
  [key: string]: any;
}

/**
 * Assessment Status Levels
 */
export type AssessmentStatus = 'on-track' | 'at-risk' | 'delayed';
export type RiskLevel = 'low' | 'medium' | 'high';

/**
 * Project Health Assessment
 */
export interface ProjectHealthAssessment {
  overallCompletion: number; // Actual completion (0-100)
  expectedCompletion: number; // Expected completion at current date
  completionGap: number; // Positive = ahead, Negative = behind
  scheduleStatus: AssessmentStatus;
  resourceStatus: {
    specsComplete: number;
    shopComplete: number;
    poComplete: number;
    totalItems: number;
  };
  riskLevel: RiskLevel;
  atRiskCount: number;
  delayedCount: number;
  onTrackCount: number;
}

/**
 * Category Performance Analysis
 */
export interface CategoryPerformance {
  category: string;
  color: string;
  totalItems: number;
  completionPercentage: number;
  expectedCompletion: number;
  completionGap: number;
  scheduleStatus: AssessmentStatus;
  atRiskCount: number;
  delayedCount: number;
  onTrackCount: number;
  totalDuration: number;
  averageDuration: number;
}

/**
 * Auto-generated Issue
 */
export interface AssessmentIssue {
  severity: 'critical' | 'warning' | 'info';
  category?: string; // null = project-level
  message: string;
  recommendation: string;
}

/**
 * Calculate item schedule status based on 3 factors:
 * 1. Item completion percentage
 * 2. Milestone completion (if has milestones)
 * 3. Time elapsed vs total duration
 */
export const calculateItemScheduleStatus = (
  item: TimelineItem,
  milestones: ItemMilestone[]
): AssessmentStatus => {
  const now = new Date();
  const startDate = new Date(item.startDate);
  const endDate = new Date(item.endDate);
  
  // Factor 1: Item completion %
  const itemCompletion = item.completion_percentage || 0;
  
  // Factor 2: Milestone completion (if exists)
  let milestoneCompletion = 0;
  if (milestones.length > 0) {
    const totalWeight = milestones.reduce((sum, m) => {
      const start = new Date(m.start_date);
      const end = new Date(m.end_date);
      return sum + (end.getTime() - start.getTime());
    }, 0);
    
    if (totalWeight > 0) {
      milestoneCompletion = milestones.reduce((sum, m) => {
        const start = new Date(m.start_date);
        const end = new Date(m.end_date);
        const weight = (end.getTime() - start.getTime()) / totalWeight;
        return sum + (m.completion_percentage * weight);
      }, 0);
    }
  }
  
  // Factor 3: Time elapsed
  const totalDuration = endDate.getTime() - startDate.getTime();
  const elapsed = now.getTime() - startDate.getTime();
  const timeProgress = totalDuration > 0 ? (elapsed / totalDuration) * 100 : 0;
  
  // Weighted average: 50% item completion, 30% milestone, 20% time
  const avgCompletion = milestones.length > 0
    ? (itemCompletion * 0.5) + (milestoneCompletion * 0.3) + (timeProgress * 0.2)
    : (itemCompletion * 0.7) + (timeProgress * 0.3);
  
  // Determine status
  if (now < startDate) {
    return 'on-track'; // Not started yet
  }
  
  if (now > endDate) {
    return itemCompletion >= 100 ? 'on-track' : 'delayed';
  }
  
  // During timeline: compare completion vs time progress
  const completionGap = timeProgress - avgCompletion;
  
  if (completionGap <= 10) return 'on-track'; // Within 10% tolerance
  if (completionGap <= 25) return 'at-risk'; // 10-25% behind
  return 'delayed'; // > 25% behind
};

/**
 * Calculate Project Health Assessment
 */
export const calculateProjectHealth = (
  items: TimelineItem[],
  milestonesByItem: Record<string, ItemMilestone[]>
): ProjectHealthAssessment => {
  if (items.length === 0) {
    return {
      overallCompletion: 0,
      expectedCompletion: 0,
      completionGap: 0,
      scheduleStatus: 'on-track',
      resourceStatus: { specsComplete: 0, shopComplete: 0, poComplete: 0, totalItems: 0 },
      riskLevel: 'low',
      atRiskCount: 0,
      delayedCount: 0,
      onTrackCount: 0
    };
  }
  
  // Overall completion (average of all items)
  const totalCompletion = items.reduce((sum, item) => sum + (item.completion_percentage || 0), 0);
  const overallCompletion = Math.round(totalCompletion / items.length);
  
  // Calculate expected completion based on timeline
  let expectedCompletion = 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const projectStartDate = new Date(
    Math.min(...items.map(item => new Date(item.startDate).getTime()))
  );
  const projectEndDate = new Date(
    Math.max(...items.map(item => new Date(item.endDate).getTime()))
  );
  
  const totalDays = Math.max(1, Math.ceil((projectEndDate.getTime() - projectStartDate.getTime()) / (1000 * 60 * 60 * 24)));
  const elapsedDays = Math.max(0, Math.ceil((today.getTime() - projectStartDate.getTime()) / (1000 * 60 * 60 * 24)));
  
  if (elapsedDays > 0) {
    expectedCompletion = Math.min(100, Math.round((elapsedDays / totalDays) * 100));
  }
  
  const completionGap = overallCompletion - expectedCompletion;
  
  // Resource status
  const resourceStatus = {
    specsComplete: items.filter(item => item.timelineData.specs.isG5Complete).length,
    shopComplete: items.filter(item => item.timelineData.shop.isG5Complete).length,
    poComplete: items.filter(item => item.timelineData.po.isG5Complete).length,
    totalItems: items.length
  };
  
  // Schedule status distribution
  let atRiskCount = 0;
  let delayedCount = 0;
  let onTrackCount = 0;
  
  items.forEach(item => {
    const status = calculateItemScheduleStatus(item, milestonesByItem[item.id] || []);
    if (status === 'at-risk') atRiskCount++;
    else if (status === 'delayed') delayedCount++;
    else onTrackCount++;
  });
  
  // Overall schedule status
  const delayedRatio = delayedCount / items.length;
  const atRiskRatio = atRiskCount / items.length;
  
  let scheduleStatus: AssessmentStatus = 'on-track';
  if (delayedRatio > 0.1) scheduleStatus = 'delayed';
  else if (atRiskRatio > 0.2) scheduleStatus = 'at-risk';
  
  // Risk level
  let riskLevel: RiskLevel = 'low';
  if (delayedRatio > 0.2 || overallCompletion < 30) riskLevel = 'high';
  else if (delayedRatio > 0.1 || atRiskRatio > 0.3 || overallCompletion < 50) riskLevel = 'medium';
  
  return {
    overallCompletion,
    expectedCompletion,
    completionGap,
    scheduleStatus,
    resourceStatus,
    riskLevel,
    atRiskCount,
    delayedCount,
    onTrackCount
  };
};

/**
 * Calculate Category Performance
 */
export const calculateCategoryPerformance = (
  items: TimelineItem[],
  milestonesByItem: Record<string, ItemMilestone[]>,
  categoryColors: Record<string, string>
): CategoryPerformance[] => {
  const categoryMap = new Map<string, {
    items: TimelineItem[];
    totalCompletion: number;
    totalDuration: number;
    atRisk: number;
    delayed: number;
    onTrack: number;
  }>();
  
  // Group by category
  items.forEach(item => {
    if (!categoryMap.has(item.category)) {
      categoryMap.set(item.category, {
        items: [],
        totalCompletion: 0,
        totalDuration: 0,
        atRisk: 0,
        delayed: 0,
        onTrack: 0
      });
    }
    
    const categoryData = categoryMap.get(item.category)!;
    categoryData.items.push(item);
    categoryData.totalCompletion += item.completion_percentage || 0;
    
    const startDate = new Date(item.startDate);
    const endDate = new Date(item.endDate);
    const duration = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    categoryData.totalDuration += duration;
    
    const status = calculateItemScheduleStatus(item, milestonesByItem[item.id] || []);
    if (status === 'at-risk') categoryData.atRisk++;
    else if (status === 'delayed') categoryData.delayed++;
    else categoryData.onTrack++;
  });
  
  // Calculate expected completion for each category
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Convert to array
  return Array.from(categoryMap.entries()).map(([category, data]) => {
    const itemCount = data.items.length;
    const completionPercentage = Math.round(data.totalCompletion / itemCount);
    
    // Calculate expected completion for this category
    let expectedCompletion = 0;
    
    // Get category timeline range
    const categoryStartDate = new Date(
      Math.min(...data.items.map(item => new Date(item.startDate).getTime()))
    );
    const categoryEndDate = new Date(
      Math.max(...data.items.map(item => new Date(item.endDate).getTime()))
    );
    
    const totalDays = Math.max(1, Math.ceil(
      (categoryEndDate.getTime() - categoryStartDate.getTime()) / (1000 * 60 * 60 * 24)
    ));
    const elapsedDays = Math.max(0, Math.ceil(
      (today.getTime() - categoryStartDate.getTime()) / (1000 * 60 * 60 * 24)
    ));
    
    if (elapsedDays > 0) {
      expectedCompletion = Math.min(100, Math.round((elapsedDays / totalDays) * 100));
    }
    
    const completionGap = completionPercentage - expectedCompletion;
    
    // Category schedule status
    const delayedRatio = data.delayed / itemCount;
    const atRiskRatio = data.atRisk / itemCount;
    let scheduleStatus: AssessmentStatus = 'on-track';
    if (delayedRatio > 0.1) scheduleStatus = 'delayed';
    else if (atRiskRatio > 0.2) scheduleStatus = 'at-risk';
    
    return {
      category,
      color: categoryColors[category] || '#6b7280',
      totalItems: itemCount,
      completionPercentage,
      expectedCompletion,
      completionGap,
      scheduleStatus,
      atRiskCount: data.atRisk,
      delayedCount: data.delayed,
      onTrackCount: data.onTrack,
      totalDuration: data.totalDuration,
      averageDuration: Math.round(data.totalDuration / itemCount)
    };
  }).sort((a, b) => b.totalItems - a.totalItems); // Sort by item count
};

/**
 * Auto-generate issues & recommendations
 */
export const generateAssessmentIssues = (
  projectHealth: ProjectHealthAssessment,
  categoryPerformance: CategoryPerformance[]
): AssessmentIssue[] => {
  const issues: AssessmentIssue[] = [];
  
  // PROJECT-LEVEL ISSUES
  
  // Critical: Low overall completion
  if (projectHealth.overallCompletion < 30) {
    issues.push({
      severity: 'critical',
      message: `Project progress is very low (${projectHealth.overallCompletion}%)`,
      recommendation: 'Timeline review and immediate resource reinforcement required'
    });
  }
  
  // Critical: High delayed ratio
  const totalItems = projectHealth.atRiskCount + projectHealth.delayedCount + 
    (projectHealth.resourceStatus.totalItems - projectHealth.atRiskCount - projectHealth.delayedCount);
  const delayedRatio = projectHealth.delayedCount / totalItems;
  
  if (delayedRatio > 0.2) {
    issues.push({
      severity: 'critical',
      message: `${projectHealth.delayedCount} items are delayed (${Math.round(delayedRatio * 100)}%)`,
      recommendation: 'Consider adjusting scope or deadline, increase monitoring'
    });
  }
  
  // Warning: At-risk items
  if (projectHealth.atRiskCount > 0) {
    issues.push({
      severity: 'warning',
      message: `${projectHealth.atRiskCount} items at risk of delay`,
      recommendation: 'Prioritize tracking these items, consider resource adjustment'
    });
  }
  
  // Warning: Low resource completion
  const resourceRatio = (
    projectHealth.resourceStatus.specsComplete +
    projectHealth.resourceStatus.shopComplete +
    projectHealth.resourceStatus.poComplete
  ) / (projectHealth.resourceStatus.totalItems * 3);
  
  if (resourceRatio < 0.5) {
    issues.push({
      severity: 'warning',
      message: `Low specs/shop/PO completion rate (${Math.round(resourceRatio * 100)}%)`,
      recommendation: 'Accelerate specs and procurement processes'
    });
  }
  
  // CATEGORY-LEVEL ISSUES
  
  categoryPerformance.forEach(cat => {
    // Critical: Category with high delayed ratio
    const catDelayedRatio = cat.delayedCount / cat.totalItems;
    if (catDelayedRatio > 0.3) {
      issues.push({
        severity: 'critical',
        category: cat.category,
        message: `Category "${cat.category}": ${cat.delayedCount}/${cat.totalItems} items delayed`,
        recommendation: 'Review dependencies and bottlenecks in this category'
      });
    }
    
    // Warning: Low category completion
    if (cat.completionPercentage < 25 && cat.totalItems >= 5) {
      issues.push({
        severity: 'warning',
        category: cat.category,
        message: `Category "${cat.category}": Low progress (${cat.completionPercentage}%)`,
        recommendation: 'Prioritize resources for this category'
      });
    }
  });
  
  // Info: Positive feedback
  if (projectHealth.scheduleStatus === 'on-track' && projectHealth.overallCompletion >= 70) {
    issues.push({
      severity: 'info',
      message: 'Project is on schedule with good progress',
      recommendation: 'Continue to maintain momentum and monitor risks'
    });
  }
  
  return issues.sort((a, b) => {
    const severityOrder = { critical: 0, warning: 1, info: 2 };
    return severityOrder[a.severity] - severityOrder[b.severity];
  });
};
